import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["filterBar", "searchInput", "colorInput"]
  static values = { lastQuery: String, isOpen: Boolean }

  connect() {
    this.captureCurrentQuery()
    this.setupEventListeners()
  }

  disconnect() {
    this.cleanupEventListeners()
  }

  setupEventListeners() {
    this.boundHandleTurboLoad = this.handleTurboLoad.bind(this)
    this.boundHandleBeforeCache = this.handleBeforeCache.bind(this)
    document.addEventListener('turbo:load', this.boundHandleTurboLoad)
    document.addEventListener('turbo:before-cache', this.boundHandleBeforeCache)
  }

  cleanupEventListeners() {
    document.removeEventListener('turbo:load', this.boundHandleTurboLoad)
    document.removeEventListener('turbo:before-cache', this.boundHandleBeforeCache)
  }

  handleTurboLoad() {
    this.captureCurrentQuery()
  }

  // Ensure filter bar is closed before page is cached by Turbo
  // This prevents stale open state from being restored on navigation
  handleBeforeCache() {
    this.hideFilterBar()
  }

  removeFilter(event) {
    event.preventDefault()
    event.stopPropagation()

    // Close filter bar if open to prevent flicker during navigation
    this.hideFilterBar()

    const { key, value } = this.getFilterDataFromEvent(event)
    const urlKey = this.normalizeUrlKey(key)

    this.captureCurrentQuery()
    const url = this.buildUpdatedUrl(urlKey, value)
    this.navigateToUrl(url)
  }

  getFilterDataFromEvent(event) {
    return {
      key: event.currentTarget.dataset.filterKey,
      value: event.currentTarget.dataset.filterValue
    }
  }

  normalizeUrlKey(key) {
    return key === "company_ids" ? "c" : key
  }

  buildUpdatedUrl(urlKey, valueToRemove) {
    // Start from current URL - this preserves starts_with and other params
    // (unlike buildUrlFromFormData which starts from form data)
    const url = new URL(window.location)
    const searchParams = new URLSearchParams(url.search)

    this.updateQueryParam(searchParams)
    this.updateFilterParams(searchParams, urlKey, valueToRemove)

    url.search = searchParams.toString()
    return url
  }

  updateQueryParam(searchParams) {
    if (this.lastQueryValue?.trim()) {
      searchParams.set('query', this.lastQueryValue)
    } else {
      searchParams.delete('query')
    }
  }

  updateFilterParams(searchParams, urlKey, valueToRemove) {
    // Handle array format (checkboxes): filters[key][]
    const arrayFilters = searchParams.getAll(`filters[${urlKey}][]`)
    const newArrayFilters = arrayFilters.filter(v => v !== valueToRemove)

    searchParams.delete(`filters[${urlKey}][]`)
    newArrayFilters.forEach(v => searchParams.append(`filters[${urlKey}][]`, v))

    // Handle non-array format (radio buttons): filters[key]
    const singleFilter = searchParams.get(`filters[${urlKey}]`)
    if (singleFilter === valueToRemove) {
      searchParams.delete(`filters[${urlKey}]`)
    }

    return newArrayFilters
  }

  navigateToUrl(url) {
    Turbo.visit(url.toString(), { action: "advance" })
  }

  submitFilter(event) {
    event.preventDefault()

    const formData = new FormData(event.target)
    this.captureCurrentQuery()
    this.appendQueryToFormData(formData)

    const url = this.buildUrlFromFormData(formData)
    this.navigateToUrl(url)
  }

  appendQueryToFormData(formData) {
    if (this.lastQueryValue?.trim()) {
      formData.append('query', this.lastQueryValue)
    }
  }

  buildUrlFromFormData(formData) {
    const searchParams = new URLSearchParams(formData)
    const url = new URL(window.location)

    // Preserve starts_with from current URL (not from stale form hidden fields)
    const currentParams = new URLSearchParams(url.search)
    const startsWithValue = currentParams.get('starts_with')
    if (startsWithValue) {
      searchParams.set('starts_with', startsWithValue)
    }

    url.search = searchParams.toString()
    return url
  }

  open(event) {
    event.preventDefault()
    this.showFilterBar()
    this.captureCurrentQuery()
    this.isOpenValue = true
  }

  close(event) {
    event.preventDefault()
    this.hideFilterBar()
    this.isOpenValue = false
  }

  showFilterBar() {
    this.filterBarTarget.classList.add("table-filter-bar--active")
  }

  hideFilterBar() {
    this.filterBarTarget.classList.remove("table-filter-bar--active")
  }

  // Color selection for filter sidebar
  selectColor(event) {
    event.preventDefault()

    const link = event.target.closest('.color-option')
    if (!link) return

    const colorValue = link.dataset.colorValue
    const circle = link.querySelector('.circle')
    const isCurrentlySelected = circle.classList.contains('selected')

    // Clear all selected states
    this.element.querySelectorAll('.circle').forEach(c => c.classList.remove('selected'))

    // If it wasn't selected, select it. If it was selected, leave it deselected
    if (!isCurrentlySelected) {
      circle.classList.add('selected')
      // Update hidden input with selected color
      if (this.hasColorInputTarget) {
        this.colorInputTarget.value = colorValue
      }
    } else {
      // Deselected - clear the hidden input
      if (this.hasColorInputTarget) {
        this.colorInputTarget.value = ''
      }
    }
  }

  captureCurrentQuery() {
    const searchInput = this.findSearchInput()
    if (searchInput && searchInput.value?.trim()) {
      this.lastQueryValue = searchInput.value
    } else {
      // Fallback: get query from current URL if search input is empty or not found
      const urlParams = new URLSearchParams(window.location.search)
      const urlQuery = urlParams.get('query')
      if (urlQuery) {
        this.lastQueryValue = urlQuery
      } else {
        // Clear lastQueryValue if both input and URL are empty
        this.lastQueryValue = ''
      }
    }
  }

  findSearchInput() {
    if (this.hasSearchInputTarget) {
      return this.searchInputTarget
    }
    return this.findSearchInputInDOM()
  }

  findSearchInputInDOM() {
    let currentElement = this.element

    while (currentElement) {
      const searchInput = this.searchInSiblings(currentElement)
      if (searchInput) return searchInput

      currentElement = currentElement.parentElement
    }

    return null
  }

  searchInSiblings(element) {
    const siblings = element.parentElement?.children || []

    for (const sibling of siblings) {
      if (sibling !== element) {
        const input = sibling.querySelector('[data-table-search-target="search"]')
        if (input) return input
      }
    }

    return null
  }
}
